import { createSlice } from '@reduxjs/toolkit';
import { movieInitialState } from '../../models';

const movieSlice = createSlice({
  name: 'movies',
  initialState: movieInitialState,
  reducers: {
    setMovies: (state, action) => {
      state.movies = action.payload;
    },
    
    addMovies: (state, action) => {
      const newMovies = action.payload;
      const existingIds = state.movies.map(m => m.id);
      const uniqueMovies = newMovies.filter(m => !existingIds.includes(m.id));
      state.movies = [...state.movies, ...uniqueMovies];
    },
    
    setTotalResults: (state, action) => {
      state.totalResults = action.payload;
    },
    
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    
    setHasMore: (state, action) => {
      state.hasMore = action.payload;
    },
    
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    
    setLoadingMore: (state, action) => {
      state.isLoadingMore = action.payload;
    },
    
    setError: (state, action) => {
      state.error = action.payload;
    },
    
    clearError: (state) => {
      state.error = null;
    },
    
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    
    resetFilters: (state) => {
      state.filters = {
        selectedGenre: 'all',
        sortBy: 'trending',
        imdbRating: 0,
        rottenTomatoesRating: 0,
        fromYear: null,
        toYear: null,
      };
    },
    
    setViewMode: (state, action) => {
      state.viewMode = action.payload;
    },
    
    addToPrefetchQueue: (state, action) => {
      const movieIds = action.payload;
      const newIds = movieIds.filter(id => 
        !state.prefetchedIds.includes(id) && 
        !state.prefetchQueue.includes(id)
      );
      state.prefetchQueue.push(...newIds);
    },
    
    setPrefetching: (state, action) => {
      state.isPrefetching = action.payload;
    },
    
    markAsPrefetched: (state, action) => {
      const movieIds = action.payload;
      movieIds.forEach(id => {
        if (!state.prefetchedIds.includes(id)) {
          state.prefetchedIds.push(id);
        }
      });
      state.prefetchQueue = state.prefetchQueue.filter(id => !movieIds.includes(id));
    },
    
    clearPrefetchQueue: (state) => {
      state.prefetchQueue = [];
    },
    
    setEnhancedData: (state, action) => {
      const { movieId, data } = action.payload;
      state.enhancedDataCache[movieId] = data;
    },
    
    bulkSetEnhancedData: (state, action) => {
      state.enhancedDataCache = { ...state.enhancedDataCache, ...action.payload };
    },
    
    clearEnhancedDataCache: (state) => {
      state.enhancedDataCache = {};
    },
    
    updatePerformanceMetrics: (state, action) => {
      const { type, value } = action.payload;
      
      if (type === 'fetchTime') {
        state.lastFetchTime = value;
      } else if (type === 'fetchCount') {
        state.fetchCount += 1;
      }
    },
    
    resetMovieState: (state) => {
      state.movies = [];
      state.totalResults = 0;
      state.currentPage = 1;
      state.hasMore = true;
      state.error = null;
      state.isLoading = false;
      state.isLoadingMore = false;
      state.prefetchQueue = [];
      state.prefetchedIds = [];
      state.isPrefetching = false;
    },
    
    sortMovies: (state, action) => {
      const sortBy = action.payload;
      
      state.movies.sort((a, b) => {
        switch (sortBy) {
          case 'vote_average':
            const aImdb = a.enhanced_ratings?.real_ratings?.imdb?.rating || 0;
            const bImdb = b.enhanced_ratings?.real_ratings?.imdb?.rating || 0;
            const aRt = a.enhanced_ratings?.real_ratings?.rottenTomatoes?.rating || 0;
            const bRt = b.enhanced_ratings?.real_ratings?.rottenTomatoes?.rating || 0;
            const aTmdb = a.vote_average || 0;
            const bTmdb = b.vote_average || 0;
            
            const aHasOnlyTmdb = !aImdb && !aRt && aTmdb;
            const bHasOnlyTmdb = !bImdb && !bRt && bTmdb;
            
            if (aHasOnlyTmdb && !bHasOnlyTmdb) return 1;
            if (!aHasOnlyTmdb && bHasOnlyTmdb) return -1;
            
            if (aHasOnlyTmdb && bHasOnlyTmdb) {
              return bTmdb - aTmdb;
            }
            
            const aRating = aImdb || (aRt / 10) || aTmdb;
            const bRating = bImdb || (bRt / 10) || bTmdb;
            return bRating - aRating;
          
          case 'revenue':
            const aRevenue = a.revenue || 0;
            const bRevenue = b.revenue || 0;
            return bRevenue - aRevenue;
          
          case 'popularity':
          default:
            return (b.popularity || 0) - (a.popularity || 0);
        }
      });
    },
    
    filterMovies: (state, action) => {
      const { imdbRating, rottenTomatoesRating } = action.payload;
      
      state.movies = state.movies.filter(movie => {
        const hasImdb = movie.enhanced_ratings?.real_ratings?.imdb?.rating;
        const hasRt = movie.enhanced_ratings?.real_ratings?.rottenTomatoes?.rating;
        const hasOnlyTmdb = !hasImdb && !hasRt && movie.vote_average;
        
        if (hasOnlyTmdb) {
          return true;
        }
        
        if (imdbRating > 0) {
          const movieImdbRating = movie.enhanced_ratings?.real_ratings?.imdb?.rating;
          if (movie.enhanced_ratings && movieImdbRating !== null && movieImdbRating !== undefined && movieImdbRating < imdbRating) {
            return false;
          }
        }
        
        if (rottenTomatoesRating > 0) {
          const movieRtScore = movie.enhanced_ratings?.real_ratings?.rottenTomatoes?.rating;
          if (movie.enhanced_ratings && movieRtScore !== null && movieRtScore !== undefined && movieRtScore < rottenTomatoesRating) {
            return false;
          }
        }
        
        return true;
      });
    },
  },
});

export const {
  setMovies,
  addMovies,
  setTotalResults,
  setCurrentPage,
  setHasMore,
  setLoading,
  setLoadingMore,
  setError,
  clearError,
  setFilters,
  resetFilters,
  setViewMode,
  addToPrefetchQueue,
  setPrefetching,
  markAsPrefetched,
  clearPrefetchQueue,
  setEnhancedData,
  bulkSetEnhancedData,
  clearEnhancedDataCache,
  setScrollPosition,
  setContentState,
  updatePerformanceMetrics,
  resetMovieState,
  sortMovies,
  filterMovies,
} = movieSlice.actions;

export default movieSlice.reducer; 